import { findRouteSeo } from '@/composables/findRouteSeo.ts';
import { gtmEvent } from '@/composables/gtm/useGtm';
import { useMeta } from '@/composables/useMeta.ts';
import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import { productRandomisationGuard } from '@/features/Product/router/productRandomisationGuard';

export const genericMetaDescription = `A global leader in oral care, Hismile, creates a range of products suited to every oral care concern, including the viral purple V34 Colour Corrector, our flavoured toothpaste range and market leading peroxide-free teeth whitening strips. Hismile is the choice of customers, and dentists globally.`;

const routes: RouteRecordRaw[] = [
  {
    path: '/password',
    name: 'password',
    component: () => import('@/views/PasswordView.vue'),
    meta: {
      title: 'Password Locked',
      description: 'Site is password protected',
    },
  },
  {
    path: '/challenge',
    name: 'challenge',
    component: () => import('@/views/PasswordView.vue'),
    meta: {
      title: 'Challenge',
      description: 'Challenge',
    },
  },
  {
    path: '/gift_cards/:id?/:token?',
    name: 'giftcards',
    component: () => import('@/views/PasswordView.vue'),
    meta: {
      title: 'Gift Cards',
      description: 'Gift Cards',
    },
    props: (route) => ({
      id: route.params.id,
      token: route.params.token,
    }),
  },
  // main layout
  {
    path: '/',
    component: () => import('@/views/MainView.vue'),
    children: [
      {
        path: '',
        name: 'home',
        component: () => import('@/views/HomeView.vue'),
        meta: {
          title: 'Hismile™ Official | At-home Oral Care',
          description: genericMetaDescription,
        },
      },
      {
        path: '/cart',
        name: 'cart',
        component: () => import('@/views/CartView.vue'),
        meta: {
          title: 'Your Shopping Cart',
          description: genericMetaDescription,
        },
      },
      {
        path: '/collections/:handle/:sortOrder?',
        name: 'collection',
        component: () => import('@/views/CollectionView.vue'),
        props: (route) => ({
          handle: route.params.handle,
          sortOrder: route.params.sortOrder || '',
        }),
      },
      {
        path: '/products/:parentHandle/:childHandle',
        name: 'product-contextual',
        component: () => import('@/views/ProductView.vue'),
        props: (route) => ({
          handle: route.params.parentHandle,
          variantHandle: route.params.childHandle,
        }),
      },
      {
        path: '/products/:handle',
        name: 'product',
        component: () => import('@/views/ProductView.vue'),
        props: (route) => ({
          handle: route.params.handle,
          variantHandle: null,
        }),
      },
      {
        path: '/pages/:handle/',
        name: 'page',
        component: () => import('@/views/PageView.vue'),
        props: (route) => ({
          handle: route.params.handle,
        }),
      },
      {
        path: '/account/:handle?/:id?/:token?',
        name: 'account',
        component: () => import('@/views/AccountView.vue'),
        props: (route) => ({
          handle: route.params.handle,
          id: route.params.id,
          token: route.params.token,
        }),
      },
      // this must always be a the end
      {
        path: '/:pathMatch(.*)*',
        name: '404',
        component: () => import('@/views/NotFoundView.vue'),
        meta: {
          title: '404 Not Found',
          description: `The page you have navigated to has not been found.`,
        },
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => false,
});

router.beforeEach(productRandomisationGuard);

router.beforeEach(async (to, from, next) => {
  const { setMeta } = useMeta();
  to.meta = {
    ...to.meta,
    gtmAdditionalEventData: await gtmEvent().requiredGtmData(),
  };
  const routeSeoMeta = findRouteSeo(to);
  const defaultMeta = {
    title: to.name as string,
    path: to.path,
    description: '',
  };

  if (routeSeoMeta?.title) {
    setMeta(routeSeoMeta);
  } else {
    setMeta({
      ...defaultMeta,
      ...to.meta,
    });
  }

  next();
});

export default router;
